<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { orderBy } from 'lodash-es'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as userGroups, type SimpleFetchResult } from 'hydria/services/session'
    import { asAny, sanchor, spath } from 'hydria/svelte_helpers'
    import { makeSortString } from 'hydria/utils/strings'

    import type { IAddress } from 'wdom/services/contacts'
    import { loadFrequentTags, type FTag } from 'admin/helpers/tags'

    import type { AdminData, User, UserHandler } from './user'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const userh = getContext('User') as UserHandler
    const selectedAddress = getContext('selectedAddress') as Writable<IAddress>

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)
    const adminData: Readable<AdminData> = sderived(userh.adminData, ($v) => $v)

    let settingLg = $state(false)

    async function setLg(newLg: 'fr' | 'en') {
        settingLg = true

        try {
            const r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/meta`, { prefered_lg: newLg })
            userh.raw.item.metadata = r.data.item.metadata
            userh.loadUserResponse()
        } catch (e) {
            console.error('Failed to set lg:', e)
        }

        settingLg = false
    }

    let settingForcedLg = $state(false)

    async function setForcedLg(value: boolean) {
        settingForcedLg = true

        try {
            const r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/meta`, { force_prefered_lg: value })
            userh.raw.item.metadata = r.data.item.metadata
            userh.loadUserResponse()
        } catch (e) {
            console.error('Failed to set lg-on-signin:', e)
        }

        settingForcedLg = false
    }

    let confirmModal: 'tag-add' | 'tag-remove' | false = $state(false)

    let globalFrequentTags: FTag[] | null = $state(null)
    loadFrequentTags('user').then((r) => {
        globalFrequentTags = r
    })

    // async function loadFrequentTags() {
    //     let r: SimpleFetchResult

    //     try {
    //         r = await simpleFetch('GET', '/json/wdom-user/1/frequent-tags')
    //     } catch (e: any) {
    //         r = e
    //     }

    //     if (r && r.success) {
    //         const tlist = Object.entries(r.data.counts)
    //             .map((v) => {
    //                 return { id: v[0], freq: v[1] as number }
    //             })
    //             .slice(0, 12)
    //         globalFrequentTags = orderBy(tlist, [(v) => v.freq, (v) => v.id], ['desc', 'asc'])
    //     }
    // }
    // loadFrequentTags()

    let sortedTags: string[] = $derived($status == 'ready' && $adminData.tags != null && $adminData.tags.length ? orderBy([...new Set($adminData.tags)], [(v) => makeSortString(v)], ['asc']) : [])

    let frequentTags: FTag[] | null = $derived.by(() => {
        if (sortedTags != null && globalFrequentTags != null) {
            const assignedTags = new Set(sortedTags)
            return globalFrequentTags.filter((ftag) => !assignedTags.has(ftag.id))
        }
        return null
    })

    let tagToRemove: string | null = $state(null)
    let tagToAdd: string = $state('')
    let tagWorking: boolean = $state(false)
    let tagInputElement: HTMLInputElement | undefined = $state()

    $effect(() => {
        if (tagInputElement) tagInputElement.focus()
    })

    function preRemoveTag(tag: string) {
        tagToRemove = tag
        confirmModal = 'tag-remove'
    }

    function preAddTag() {
        tagToAdd = ''
        confirmModal = 'tag-add'
    }

    async function removeTag(tag: string) {
        const nTags = new Set(sortedTags)
        nTags.delete(tag)

        try {
            tagWorking = true
            await userh.saveTags(nTags)
            tagWorking = false
            notify({
                type: 'success',
                title: 'Tag',
                text: `Le tag « ${tag} » a été supprimé.`,
            })
            confirmModal = false
        } catch (error) {
            tagWorking = false
            notify({
                type: 'danger',
                title: 'Tag',
                text: `Le tag « $${tag} » n’a pas pu être enlevé: ${error}`,
            })
        }
    }

    async function addTag(tag: string) {
        const nTags = new Set(sortedTags)
        nTags.add(tag)

        try {
            tagWorking = true
            await userh.saveTags(nTags)
            tagWorking = false
            notify({
                type: 'success',
                title: 'Tag',
                text: `Le tag « ${tag} » a été ajouté.`,
            })
            confirmModal = false
        } catch (error) {
            tagWorking = false
            notify({
                type: 'danger',
                title: 'Tag',
                text: `Le tag « ${tag} » n’a pas pu être ajouté: ${error}`,
            })

            if (tagInputElement) tagInputElement.focus()
        }
    }

    $effect(() => {
        if (confirmModal !== false && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (confirmModal === false && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (confirmModal !== false && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    dd {
        margin-bottom: 0;
    }

    dt:not(:first-child) {
        margin-top: 0.5rem;
    }

    .ulg {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
    }

    .tag-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        width: 100%;
        justify-content: end;
    }
    .tag-tags .tag-sep {
        flex: 1 0 0;
    }

    .freq-tags {
        display: flex;
        gap: 0.25rem;
        flex-wrap: wrap;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (confirmModal !== false) {
                confirmModal = false
            }
        }
    }}
/>

<div class="card">
    <div class="card-title text-primary">
        {#if $selectedAddress && $selectedAddress.accounting_comment}
            <span class="float-end">{$selectedAddress.accounting_comment}</span>
        {/if}
        {$_('User information')}
    </div>

    {#if $status == 'loading' || $loading}
        <div class="card-body">
            <LoadingPattern pattern={[25, 25, 25, 25, 25, 25, '-', 20, 20, '-', '-', ['flex', ['-:1 0 0', 'b:0 0 150px']]]} />
        </div>
    {:else}
        <div class="card-body">
            <div class="float-end"><a use:sanchor href={spath(`/wdom-user/${$user.id}/extra-meta`)}>Métadonnées supplémentaires</a></div>

            <dl>
                <dt>{$_('Création du compte')}</dt>
                <dd><Date value={$user.ctime} format="table-s" /></dd>

                <dt>{$_('Dernière modification')}</dt>
                <dd><Date value={$user.mtime} format="table-s" /></dd>

                <dt>{$_('Dernière connexion')}</dt>
                <dd>
                    {#if $user.last_signed_in}
                        <Date value={$user.last_signed_in} format="table-s" />
                    {:else}
                        {$_('Ne s’est jamais connecté')}
                    {/if}
                </dd>

                <dt class="margin-top">{$_('Langue préférée')}</dt>
                <dd class="ulg">
                    <div>
                        <div class="btn-group btn-group-sm">
                            <button type="button" class="btn btn-outline-secondary" class:active={$user.metadata && $user.metadata.prefered_lg == 'fr'} class:btn-primary={$user.metadata && $user.metadata.prefered_lg == 'fr'} disabled={settingLg} onclick={() => setLg('fr')}>Français</button>
                            <button type="button" class="btn btn-outline-secondary" class:active={$user.metadata && $user.metadata.prefered_lg == 'en'} class:btn-primary={$user.metadata && $user.metadata.prefered_lg == 'en'} disabled={settingLg} onclick={() => setLg('en')}>Anglais</button>
                            {#if settingLg}
                                <button type="button" class="btn btn-info" disabled aria-hidden="true"><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></button>
                            {/if}
                        </div>
                    </div>
                    <div>
                        <div class="btn-group btn-group-sm">
                            <button type="button" class="btn btn-outline-secondary" class:active={$user.metadata && $user.metadata.force_prefered_lg} class:btn-primary={$user.metadata && $user.metadata.force_prefered_lg} disabled={settingForcedLg} onclick={() => setForcedLg(true)}>Activer la langue sur connexion</button>
                            <button type="button" class="btn btn-outline-secondary" class:active={$user.metadata && !$user.metadata.force_prefered_lg} class:btn-primary={$user.metadata && !$user.metadata.force_prefered_lg} disabled={settingForcedLg} onclick={() => setForcedLg(false)}>Conserver la langue en cours</button>
                        </div>
                    </div>
                </dd>

                <dt class="margin-top">Tags</dt>
                <dd>
                    <div class="tag-tags">
                        {#if sortedTags != null && sortedTags.length}
                            {#each sortedTags as tag}
                                <div class="tag">
                                    <button type="button" class="btn btn-sm btn-outline-primary" data-tooltip={$_('Enlever « {tag} »', { tag: tag })} onclick={() => preRemoveTag(tag)}>{tag}</button>
                                </div>
                            {/each}
                        {:else}
                            <div class="notag">Aucun tag</div>
                        {/if}
                        <!-- <div class="tag-sep"></div> -->
                        <div class="tag-add">
                            <button type="button" class="btn btn-sm btn-primary" data-tooltip={$_('Ajouter un tag')} onclick={() => preAddTag()}>Ajouter</button>
                        </div>
                    </div>
                </dd>
            </dl>
        </div>
        {#if $userGroups.has('send-custom-emails')}
            <div class="card-footer text-end">
                <a use:sanchor href={spath(`/wdom-user/${$user.id}/custom-email`)} class="btn btn-outline-secondary">{$_('Envoyer un courriel')}</a>
            </div>
        {/if}
    {/if}
</div>

{#if confirmModal == 'tag-remove'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }} onclick={() => (confirmModal = false)} aria-hidden="true">
        <div
            class="modal-dialog"
            onclick={(e) => {
                e.stopPropagation()
            }}
            aria-hidden="true"
        >
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>Enlever un tag</div>
                </div>
                <div class="modal-body">
                    <p>Enlever le <em>tag</em> « {tagToRemove} » ?</p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" disabled={tagWorking} onclick={() => (confirmModal = false)}>{$_('Cancel')}</button>
                    <button type="button" class="btn btn-danger" disabled={tagWorking} onclick={() => removeTag(asAny(tagToRemove))}>{$_('Delete')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    </div>
{/if}
{#if confirmModal == 'tag-add'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }} onclick={() => (confirmModal = false)} aria-hidden="true">
        <div
            class="modal-dialog"
            onclick={(e) => {
                e.stopPropagation()
            }}
            aria-hidden="true"
        >
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    addTag(tagToAdd.trim())
                }}
            >
                <div class="modal-content">
                    <div class="modal-header text-bg-primary">
                        <div>Ajouter un tag</div>
                    </div>
                    <div class="modal-body">
                        <input type="text" class="form-control" bind:value={tagToAdd} bind:this={tagInputElement} disabled={tagWorking} />
                        <div class="help-block">Le <em>tag</em> sera ajouté tel quel incluant la casse et caractères spéciaux. La recherche tiendra compte de ces particularités.</div>
                        {#if frequentTags && frequentTags.length}
                            <div class="mt-3">
                                <div>Tags fréquents:</div>
                                <div class="freq-tags">
                                    {#each frequentTags as tag}
                                        <button type="button" class="btn btn-sm btn-link" onclick={() => (tagToAdd = tag.id)}>{tag.id} ({tag.freq})</button>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" disabled={tagWorking} onclick={() => (confirmModal = false)}>{$_('Cancel')}</button>
                        <button type="submit" class="btn btn-danger" disabled={tagWorking || tagToAdd.trim() == ''}>{$_('Add')}</button>
                    </div>
                </div>
            </form>
        </div>
        <div class="modal-backdrop"></div>
    </div>
{/if}
